import { useState, useEffect, useMemo } from 'react';
import { Layout } from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { productsAPI } from '../lib/api';
import { formatNumber, formatCurrency, getCategoryLabel } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Warehouse, 
  Storefront, 
  MagnifyingGlass,
  DownloadSimple,
  Package,
  CurrencyCircleDollar,
  CookingPot
} from '@phosphor-icons/react';

export default function StockReport() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await productsAPI.getAll();
      setProducts(res.data);
    } catch (error) {
      toast.error('Ürünler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Kategorileri çıkar
  const categories = useMemo(() => {
    const cats = [...new Set(products.map(p => p.category))];
    return cats.filter(Boolean).sort();
  }, [products]);

  // Filtrelenmiş ürünler
  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      const matchesSearch = !searchQuery || 
        p.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || p.category === categoryFilter;
      
      // prep_stock için varsayılan değer
      const prepStock = p.prep_stock || 0;
      
      if (locationFilter === 'warehouse') {
        return matchesSearch && matchesCategory && p.warehouse_stock > 0;
      } else if (locationFilter === 'prep') {
        return matchesSearch && matchesCategory && prepStock > 0;
      } else if (locationFilter === 'store') {
        return matchesSearch && matchesCategory && p.store_stock > 0;
      } else if (locationFilter === 'zero') {
        // Üç lokasyonda da stok yoksa
        return matchesSearch && matchesCategory && 
          (p.warehouse_stock === 0 && prepStock === 0 && p.store_stock === 0);
      }
      return matchesSearch && matchesCategory;
    });
  }, [products, searchQuery, categoryFilter, locationFilter]);

  // Toplam değerler - prep_stock dahil
  const totals = useMemo(() => {
    const warehouseTotal = filteredProducts.reduce((sum, p) => sum + (p.warehouse_stock * p.unit_price), 0);
    const prepTotal = filteredProducts.reduce((sum, p) => sum + ((p.prep_stock || 0) * p.unit_price), 0);
    const storeTotal = filteredProducts.reduce((sum, p) => sum + (p.store_stock * p.unit_price), 0);
    
    const totalQuantityWarehouse = filteredProducts.reduce((sum, p) => sum + p.warehouse_stock, 0);
    const totalQuantityPrep = filteredProducts.reduce((sum, p) => sum + (p.prep_stock || 0), 0);
    const totalQuantityStore = filteredProducts.reduce((sum, p) => sum + p.store_stock, 0);
    
    return {
      warehouseValue: warehouseTotal,
      prepValue: prepTotal,
      storeValue: storeTotal,
      totalValue: warehouseTotal + prepTotal + storeTotal,
      warehouseQty: totalQuantityWarehouse,
      prepQty: totalQuantityPrep,
      storeQty: totalQuantityStore,
    };
  }, [filteredProducts]);

  // Excel'e aktar
  const handleExport = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    window.open(`${backendUrl}/api/products/export/excel`, '_blank');
    toast.success('Stok raporu indiriliyor...');
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">
              Stok Raporu
            </h1>
            <p className="text-muted-foreground mt-1">
              Depo, hazırlık mutfağı ve mağaza stok durumlarını görüntüleyin
            </p>
          </div>
          <Button onClick={handleExport} variant="outline" data-testid="export-excel-btn">
            <DownloadSimple size={20} weight="bold" className="mr-2" />
            Excel İndir
          </Button>
        </div>

        {/* Summary Cards - 5 kart */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {/* Depo Stok Değeri */}
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <Warehouse size={24} className="text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Depo Stok</p>
                <p className="text-lg font-bold text-blue-600" data-testid="warehouse-value">{formatCurrency(totals.warehouseValue)}</p>
              </div>
            </div>
          </div>
          
          {/* Hazırlık Mutfağı Stok Değeri */}
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100">
                <CookingPot size={24} className="text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Hazırlık Stok</p>
                <p className="text-lg font-bold text-amber-600" data-testid="prep-value">{formatCurrency(totals.prepValue)}</p>
              </div>
            </div>
          </div>
          
          {/* Mağaza Stok Değeri */}
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <Storefront size={24} className="text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Mağaza Stok</p>
                <p className="text-lg font-bold text-green-600" data-testid="store-value">{formatCurrency(totals.storeValue)}</p>
              </div>
            </div>
          </div>
          
          {/* Toplam Stok Değeri */}
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <CurrencyCircleDollar size={24} className="text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Toplam Değer</p>
                <p className="text-lg font-bold text-primary" data-testid="total-value">{formatCurrency(totals.totalValue)}</p>
              </div>
            </div>
          </div>
          
          {/* Toplam Ürün */}
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-100">
                <Package size={24} className="text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Toplam Ürün</p>
                <p className="text-lg font-bold text-orange-600" data-testid="total-products">{filteredProducts.length} Ürün</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Ürün ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="stock-report-search"
            />
          </div>
          
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-48" data-testid="category-filter">
              <SelectValue placeholder="Kategori" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Kategoriler</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat} value={cat}>{getCategoryLabel(cat)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={locationFilter} onValueChange={setLocationFilter}>
            <SelectTrigger className="w-48" data-testid="location-filter">
              <SelectValue placeholder="Konum" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Stoklar</SelectItem>
              <SelectItem value="warehouse">Sadece Depoda</SelectItem>
              <SelectItem value="prep">Sadece Hazırlık</SelectItem>
              <SelectItem value="store">Sadece Mağazada</SelectItem>
              <SelectItem value="zero">Stoksuz Ürünler</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Stock Table */}
        <div className="border border-border bg-card rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-border bg-muted/30">
                  <TableHead className="py-3 font-semibold">Ürün Adı</TableHead>
                  <TableHead className="font-semibold">Kategori</TableHead>
                  <TableHead className="font-semibold">Birim</TableHead>
                  <TableHead className="text-right font-semibold">Birim Fiyat</TableHead>
                  <TableHead className="text-right font-semibold">
                    <div className="flex items-center justify-end gap-1">
                      <Warehouse size={16} />
                      Depo
                    </div>
                  </TableHead>
                  <TableHead className="text-right font-semibold">
                    <div className="flex items-center justify-end gap-1">
                      <CookingPot size={16} />
                      Hazırlık
                    </div>
                  </TableHead>
                  <TableHead className="text-right font-semibold">
                    <div className="flex items-center justify-end gap-1">
                      <Storefront size={16} />
                      Mağaza
                    </div>
                  </TableHead>
                  <TableHead className="text-right font-semibold">Toplam Miktar</TableHead>
                  <TableHead className="text-right font-semibold">Toplam Değer</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProducts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      Ürün bulunamadı
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredProducts.map((product) => {
                    const prepStock = product.prep_stock || 0;
                    const totalQty = product.warehouse_stock + prepStock + product.store_stock;
                    const totalValue = totalQty * product.unit_price;
                    
                    return (
                      <TableRow key={product.id} className="border-b border-border/40 hover:bg-muted/20" data-testid={`product-row-${product.id}`}>
                        <TableCell className="py-3 font-medium">{product.name}</TableCell>
                        <TableCell>
                          <span className="badge-pill bg-muted text-muted-foreground">
                            {getCategoryLabel(product.category)}
                          </span>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{product.unit}</TableCell>
                        <TableCell className="text-right font-data">{formatCurrency(product.unit_price)}</TableCell>
                        <TableCell className="text-right">
                          <span className={`font-data ${product.warehouse_stock === 0 ? 'text-muted-foreground' : 'text-blue-600 font-semibold'}`}>
                            {formatNumber(product.warehouse_stock)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={`font-data ${prepStock === 0 ? 'text-muted-foreground' : 'text-amber-600 font-semibold'}`}>
                            {formatNumber(prepStock)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={`font-data ${product.store_stock === 0 ? 'text-muted-foreground' : 'text-green-600 font-semibold'}`}>
                            {formatNumber(product.store_stock)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-data font-semibold">
                          {formatNumber(totalQty)}
                        </TableCell>
                        <TableCell className="text-right font-data font-semibold text-primary">
                          {formatCurrency(totalValue)}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
              {/* Footer with totals */}
              <tfoot>
                <TableRow className="bg-muted/50 border-t-2 border-border">
                  <TableCell colSpan={4} className="py-3 font-bold text-right">TOPLAM:</TableCell>
                  <TableCell className="text-right font-bold text-blue-600">
                    {formatCurrency(totals.warehouseValue)}
                  </TableCell>
                  <TableCell className="text-right font-bold text-amber-600">
                    {formatCurrency(totals.prepValue)}
                  </TableCell>
                  <TableCell className="text-right font-bold text-green-600">
                    {formatCurrency(totals.storeValue)}
                  </TableCell>
                  <TableCell className="text-right font-bold">
                    {formatNumber(totals.warehouseQty + totals.prepQty + totals.storeQty)}
                  </TableCell>
                  <TableCell className="text-right font-bold text-primary text-lg">
                    {formatCurrency(totals.totalValue)}
                  </TableCell>
                </TableRow>
              </tfoot>
            </Table>
          </div>
        </div>
      </div>
    </Layout>
  );
}
